import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { IntragroupServiceLaunchBody } from "components/IntragroupServices/IntragroupServiceLaunchBody";
import { Form, Formik, FormikValues } from "formik";
import { addTaskSchema } from "formValidations/addTaskSchema";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useCreateTaskMutation } from "store/apis/getTemplates";
import { closeSalesTaxTask } from "store/slices/SalesTaxService";
import { RootState } from "store/store";
import { BackendError } from "types/utils/error";

export const SalesTaxServiceModal = ({
  isOpen,
  parentRoute,
}: {
  isOpen: boolean;
  parentRoute: string;
}) => {
  const { successToast, alertToast } = useToast();
  const { selectedSalesTaxTaskTemplate, callbackUrlOnModalClose } = useSelector(
    (state: RootState) => state.salexTaxList
  );
  const { uuid: groupId } = useCurrentGroupContext();
  const [createTask, { isLoading: isTaskCreating }] = useCreateTaskMutation();
  const history = useHistory();
  const { isCustomer } = useRoleBasedView();
  const entityId = useCurrentEntityId();
  const dispatch = useDispatch();
  const {
    title,
    approx_price: amount,
    uuid,
    description,
    seasons,
    default_task_template,
  } = selectedSalesTaxTaskTemplate;
  const { entities } = useCurrentGroupContext();

  const handleSubmit = async (values: FormikValues) => {
    try {
      const { entity, season } = values;
      let payload: { base_task_template_id: string; season?: string } = {
        base_task_template_id: uuid as string,
      };

      if (!default_task_template) {
        payload = { ...payload, season };
      }

      const task = await createTask({
        entity,
        metaData: payload,
      }).unwrap();
      successToast({ message: "Service created!" });
      dispatch(closeSalesTaxTask());
      const redirectUrl = isCustomer
        ? `/${parentRoute}/services/${task.uuid}`
        : `/tax/filings/${task.uuid}`;

      history.replace(redirectUrl);
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const handleModalClose = () => {
    if (callbackUrlOnModalClose) {
      history.push(callbackUrlOnModalClose);
    }
    dispatch(closeSalesTaxTask());
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={handleModalClose} modal={false}>
      <Modal.Content useCustomOverlay>
        <Modal.Header>
          <Modal.Title>{title}</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Formik
          onSubmit={handleSubmit}
          initialValues={{
            entity: entities?.[0]?.uuid || "",
            season: seasons?.[0] || "",
          }}
          validationSchema={addTaskSchema}
        >
          <Form className="all:unset">
            <Modal.Body>
              <IntragroupServiceLaunchBody
                amount={amount}
                description={description}
                seasons={seasons}
                defaultTaskTemplate={default_task_template}
              />
            </Modal.Body>
            <Modal.Footer className="t-justify-end t-flex">
              <Button
                customType="primary"
                isLoading={isTaskCreating}
                disabled={isTaskCreating}
                type="submit"
              >
                Start now
              </Button>
            </Modal.Footer>
          </Form>
        </Formik>
      </Modal.Content>
    </Modal.Root>
  );
};
